// Start — Azzurro's home / orientation page.
//
// Repurposed from the old "Grammar Map" dashboard. The verb-corpus statistics
// (group-breakdown bars, tense coverage) moved out: that material now lives in
// the dedicated Grammar / Verbs sections, so duplicating it here only cluttered
// the landing page. What remains is pure orientation, following the home-page
// research in output/verde-home-and-search-report.md:
//   • A calm, text-driven hero (name + tagline + the global search entry) — no
//     full-screen hero, so the CEFR cards peek above the fold and invite scroll.
//   • Three CEFR learning-path cards (A1 / A2 / B1) as the visual centrepiece,
//     each carrying a scope count and the path's article links. Scope, never
//     score: we show "how much is here / where to begin", never "% complete".
//   • A browse-by-pillar grid (Grammar / Verbs / Vocabulary / Expressions /
//     Practice / Reference) — the "whole content of the app" at a glance, each a
//     whole-card-clickable link.
//   • The signed-in user's pinned workspace (unchanged).
import { fetchVerbs, fetchArticles, fetchVocabulary, fetchDictionary, fetchWorkspace, overlay } from "../lib/data.js";
import { fetchAiProfile } from "../lib/ai.js";
import { EXPRESSIONS } from "../data/expressions.js";
import { isAuthed } from "../lib/auth.js";
import { navigate } from "../router/router.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { escapeHtml } from "../ui/helpers.js";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// The orientation read + the "before the grammar" basics. These are surfaced in
// their own Foundations band and deliberately pulled OUT of the CEFR path cards
// so they don't appear twice. Order = suggested reading order.
const FOUNDATION_SLUGS = [
  "how-to-learn-italian",
  "cognates",
  "apostrophe-and-elision",
  "negation",
  "possessives-and-demonstratives",
  "preposition-contractions",
  "ci-e-ci-sono",
  "sapere-vs-conoscere",
];

const LEVEL_META = {
  A1: { label: "Beginner", emoji: "🌱", blurb: "Foundations — being, gender, articles, the present.", wash: "from-sage-50 to-white", badge: "bg-sage-100 text-sage-700", ring: "hover:border-sage-300" },
  A2: { label: "Elementary", emoji: "🌿", blurb: "Past tenses, prepositions, reflexives, pronouns.", wash: "from-amber-50 to-white", badge: "bg-amber-100 text-amber-700", ring: "hover:border-amber-300" },
  B1: { label: "Intermediate", emoji: "🌳", blurb: "Mood & hypotheticals — subjunctive, conditional.", wash: "from-rose-50 to-white", badge: "bg-rose-100 text-rose-700", ring: "hover:border-rose-300" },
  B2: { label: "Upper-Intermediate", emoji: "🌲", blurb: "Nuance — compound tenses, passive, reported speech.", wash: "from-violet-50 to-white", badge: "bg-violet-100 text-violet-700", ring: "hover:border-violet-300" },
  C1: { label: "Advanced", emoji: "🏔️", blurb: "Fluency & register — idiom, subtlety, style.", wash: "from-sky-50 to-white", badge: "bg-sky-100 text-sky-700", ring: "hover:border-sky-300" },
  C2: { label: "Mastery", emoji: "⭐", blurb: "Near-native command across every context.", wash: "from-slate-50 to-white", badge: "bg-slate-100 text-slate-700", ring: "hover:border-slate-300" },
};

const CATEGORY_LABEL = {
  start: "Start here",
  verbs: "Verbs",
  nouns: "Nouns",
  structure: "Structure",
  articles: "Articles",
  sounds: "Sounds",
  words: "Words",
  sentences: "Sentences",
  prepositions: "Prepositions",
};

function articleLink(a) {
  const cat = CATEGORY_LABEL[a.category] || a.category || "";
  const isStub = /\bstub\b/i.test(a.content_markdown || "");
  const target = a.viewNav ? `data-nav-to="${escapeHtml(a.viewNav)}"` : `data-article="${escapeHtml(a.slug)}"`;
  return `
    <button ${target}
      class="w-full text-left rounded-lg px-3 py-2 transition flex items-center justify-between gap-2 hover:bg-white/70">
      <span class="min-w-0">
        <span class="block text-sm font-medium text-sage-800 truncate">${a.is_pinned ? "📌 " : ""}${escapeHtml(a.title)}</span>
        <span class="block text-xs text-sage-400">${escapeHtml(cat)}${isStub ? " · coming soon" : ""}</span>
      </span>
      <span class="text-sage-300">→</span>
    </button>`;
}

// A CEFR learning-path card: visual header (emoji + level + scope count) plus
// the path's article links. The header communicates scope; the links are the
// path itself. No progress bar, no percentage — scope, not score.
function pathCard(level, articles, starterDone) {
  const meta = LEVEL_META[level] || { label: level, emoji: "•", blurb: "", wash: "from-sage-50 to-white", badge: "bg-sage-100 text-sage-700" };
  // A1 only: a prominent "Start here" entry that opens the First Words article.
  const starter =
    level === "A1"
      ? `<button data-article="a1-first-words" class="w-full text-left rounded-lg px-3 py-2 mb-1 flex items-center justify-between gap-2 transition ${
          starterDone ? "bg-emerald-50 border border-emerald-100" : "bg-sage-100/70 hover:bg-sage-100"
        }">
           <span class="text-sm font-semibold text-sage-800">${starterDone ? "✓ First words" : "🚀 Start here · First words"}</span>
           <span class="text-xs text-sage-400">${starterDone ? "done" : "begin"}</span>
         </button>`
      : "";
  const links = articles.length
    ? articles.map(articleLink).join("")
    : starter
      ? ""
      : `<p class="text-xs text-sage-400 italic px-3 py-2">More articles coming to this level soon.</p>`;
  return `
    <section class="bg-gradient-to-b ${meta.wash} border border-sage-100 rounded-2xl p-5 flex flex-col shrink-0 w-[280px]">
      <div class="flex items-center gap-3 mb-1">
        <span class="text-2xl" aria-hidden="true">${meta.emoji}</span>
        <div>
          <div class="flex items-center gap-2">
            <span class="text-[11px] font-bold px-2 py-0.5 rounded-full ${meta.badge}">${level}</span>
            <h3 class="font-semibold text-sage-800">${meta.label}</h3>
          </div>
        </div>
        <span class="ml-auto text-xs text-sage-400 tabular-nums">${articles.length} ${articles.length === 1 ? "topic" : "topics"}</span>
      </div>
      <p class="text-xs text-sage-500 mb-3">${meta.blurb}</p>
      <div class="space-y-0.5">${starter}${links}</div>
    </section>`;
}

// A browse-by-pillar card — whole-card-clickable, single action (navigate).
// One H-level heading + one button, no nested interactive elements.
function pillarCard(p) {
  const scope = p.scope != null ? `<span class="text-xs text-sage-400 tabular-nums">${p.scope}</span>` : "";
  return `
    <button data-nav-to="${p.nav}"${p.params ? ` data-params="${escapeHtml(JSON.stringify(p.params))}"` : ""}
      class="group text-left bg-white border border-sage-100 rounded-2xl p-5 transition transform hover:-translate-y-0.5 hover:shadow-md hover:border-sage-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sage-400 focus-visible:outline-offset-2 flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-2xl" aria-hidden="true">${p.emoji}</span>
        ${scope}
      </div>
      <h3 class="font-semibold text-sage-800">${escapeHtml(p.title)}</h3>
      <p class="text-sm text-sage-500 leading-snug">${escapeHtml(p.desc)}</p>
      <span class="text-sm text-sage-400 group-hover:text-sage-600 mt-auto pt-1">Open →</span>
    </button>`;
}

function pinnedSection(verbs, articles) {
  const pinnedVerbs = verbs.filter((v) => v.is_pinned);
  const pinnedArticles = articles.filter((a) => a.is_pinned);
  if (!pinnedVerbs.length && !pinnedArticles.length) {
    return `
      <div class="bg-white border border-dashed border-sage-200 rounded-2xl p-5 text-center">
        <p class="text-sage-600 text-sm">Nothing pinned yet.</p>
        <p class="text-xs text-sage-400 mt-1">Pin verbs and articles to build your personal workspace — it follows you across devices.</p>
      </div>`;
  }
  const verbChips = pinnedVerbs
    .map(
      (v) =>
        `<button data-verb="${escapeHtml(v.infinitive)}" class="text-sm px-3 py-1.5 rounded-full bg-sage-50 border border-sage-200 text-sage-700 hover:border-sage-400">${v.emoji ? v.emoji + " " : ""}${escapeHtml(v.infinitive)}</button>`
    )
    .join("");
  const artChips = pinnedArticles
    .map(
      (a) =>
        `<button data-article="${escapeHtml(a.slug)}" class="text-sm px-3 py-1.5 rounded-full bg-white border border-sage-200 text-sage-700 hover:border-sage-400">${escapeHtml(a.title)}</button>`
    )
    .join("");
  return `
    <div class="bg-white border border-sage-100 rounded-2xl p-5">
      ${pinnedVerbs.length ? `<p class="text-xs font-semibold text-sage-500 uppercase tracking-wide mb-2">Pinned verbs</p><div class="flex flex-wrap gap-2 mb-4">${verbChips}</div>` : ""}
      ${pinnedArticles.length ? `<p class="text-xs font-semibold text-sage-500 uppercase tracking-wide mb-2">Pinned articles</p><div class="flex flex-wrap gap-2">${artChips}</div>` : ""}
    </div>`;
}

export async function dashboardView(app) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading…</div>`;

  const [verbs, articles, vocab, dictionary, workspace, profile] = await Promise.all([
    fetchVerbs(),
    fetchArticles(),
    fetchVocabulary().catch(() => []),
    fetchDictionary().catch(() => []),
    fetchWorkspace(),
    fetchAiProfile().catch(() => null),
  ]);
  const starterDone = !!(profile && profile.a1_starter_done);
  const oVerbs = overlay(verbs, workspace, "verbs");
  const oArticles = overlay(articles, workspace, "grammar_articles");

  // Scope counts — descriptive only ("how much is here"), never per-user.
  const verbCount = oVerbs.length;
  const articleCount = articles.length;
  const vocabCount = Array.isArray(vocab) ? vocab.length : 0;
  const dictCount = Array.isArray(dictionary) ? dictionary.length : 0;
  const exprCount = EXPRESSIONS.length;

  const bySlug = new Map(oArticles.map((a) => [a.slug, a]));
  const foundationArticles = FOUNDATION_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
  // Sentence Structure now lives as a dedicated Explore page (consolidated from
  // the retired "How Italian Sentences Work" article). Surface it in Foundations
  // as a view link rather than an article card, keeping its original 3rd slot.
  foundationArticles.splice(2, 0, {
    slug: "structure",
    title: "How Italian Sentences Work",
    category: "sentences",
    viewNav: "structure",
  });

  const byLevel = LEVELS.map((lvl) => ({
    level: lvl,
    items: oArticles
      .filter((a) => a.difficulty_level === lvl && a.slug !== "a1-first-words" && !FOUNDATION_SLUGS.includes(a.slug))
      .sort((a, b) => (Number(a.path_order) || 9999) - (Number(b.path_order) || 9999) || a.title.localeCompare(b.title)),
  }));

  const PILLARS = [
    { emoji: "📖", title: "Grammar", desc: "The whole-grammar guide — nouns, adjectives, sentence structure.", nav: "guide", scope: `${articleCount} articles` },
    { emoji: "🔤", title: "Verbs", desc: "Search and fully conjugate any verb across every tense and mood.", nav: "search", scope: `${verbCount.toLocaleString()} verbs` },
    { emoji: "🗂️", title: "Vocabulary", desc: "Themed word cards with examples, gender, and audio.", nav: "vocabulary", scope: vocabCount ? `${vocabCount} words` : null },
    { emoji: "📕", title: "Dictionary", desc: "Look up any verb or A1–B1 word — meanings, pronunciation, examples.", nav: "dictionary", scope: dictCount ? `${dictCount.toLocaleString()} words` : null },
    { emoji: "🎭", title: "Expressions", desc: "Idioms and set phrases grouped by the verb at their core.", nav: "expressions", scope: `${exprCount} phrases` },
    { emoji: "✍️", title: "Practice", desc: "Calm, untimed drills — conjugation, cloze, spot-the-slip, review.", nav: "trainer", scope: null },
    { emoji: "🖼️", title: "Reference shelf", desc: "Infographic posters and at-a-glance cheat sheets.", nav: "reference", scope: null },
  ];

  app.innerHTML = `
    <div class="fade-in space-y-12">
      <section class="bg-gradient-to-b from-sage-50/70 to-transparent rounded-3xl px-4 sm:px-8 py-10 text-center">
        <div class="text-4xl mb-3" aria-hidden="true">🌿</div>
        <h1 class="text-3xl sm:text-4xl font-bold text-sage-800">A calm handbook for Italian grammar</h1>
        <p class="text-sage-600 mt-3 max-w-2xl mx-auto">Look anything up in an instant, or follow a path from A1 to B1. A quiet reference — no streaks, no scores, no pressure.</p>
        <button data-cmdk-open type="button"
          class="mt-6 w-full max-w-lg mx-auto flex items-center gap-3 text-left px-4 py-3 rounded-xl border border-sage-200 bg-white text-sage-500 shadow-sm hover:border-sage-300 hover:shadow transition">
          <span aria-hidden="true">🔍</span>
          <span class="flex-1">Search verbs, grammar, vocabulary, expressions…</span>
          <kbd class="text-[0.7rem] text-sage-400 bg-sage-50 border border-sage-100 rounded px-1.5 py-0.5">⌘K</kbd>
        </button>
      </section>

      <section aria-label="Foundations">
        <div class="flex items-end justify-between mb-3">
          <div>
            <div class="flex items-center gap-2">
              <span class="text-xl" aria-hidden="true">🧭</span>
              <h2 class="text-sm font-semibold text-sage-500 uppercase tracking-wide">Foundations · Start here</h2>
            </div>
            <p class="text-xs text-sage-400 mt-0.5">How learning works, and the building blocks that come before the grammar paths.</p>
          </div>
          <button data-article="how-to-learn-italian" class="text-sm font-medium text-sage-600 hover:text-sage-800 shrink-0">How to learn Italian →</button>
        </div>
        <div class="bg-gradient-to-b from-sage-50 to-white border border-sage-100 rounded-2xl p-4 sm:p-5">
          <div class="grid gap-1.5" style="grid-template-columns:repeat(auto-fill,minmax(240px,1fr));">
            ${foundationArticles.map(articleLink).join("")}
          </div>
        </div>
      </section>

      <section>
        <div class="mb-3">
          <h2 class="text-sm font-semibold text-sage-500 uppercase tracking-wide">Learning paths</h2>
          <p class="text-xs text-sage-400 mt-0.5">Levels A1 to C2, each a short sequence of topics. Scroll right for the higher levels.</p>
        </div>
        <div class="flex gap-4 overflow-x-auto pb-2 items-stretch">
          ${byLevel.map((p) => pathCard(p.level, p.items, p.level === "A1" ? starterDone : false)).join("")}
        </div>
      </section>

      <section>
        <h2 class="text-sm font-semibold text-sage-500 uppercase tracking-wide mb-3">Explore the whole app</h2>
        <div class="grid gap-4" style="grid-template-columns:repeat(auto-fill,minmax(260px,1fr));">
          ${PILLARS.map(pillarCard).join("")}
        </div>
      </section>
    </div>`;

  // Wiring.
  app.querySelectorAll("[data-article]").forEach((b) =>
    b.addEventListener("click", () => navigate("article", { slug: b.dataset.article }))
  );
  app.querySelectorAll("[data-verb]").forEach((b) =>
    b.addEventListener("click", () => navigate("verb-detail", { inf: b.dataset.verb }))
  );
  app.querySelectorAll("[data-nav-to]").forEach((b) =>
    b.addEventListener("click", () => {
      let params = {};
      if (b.dataset.params) {
        try {
          params = JSON.parse(b.dataset.params);
        } catch {
          params = {};
        }
      }
      navigate(b.dataset.navTo, params);
    })
  );
}
